/**
 * The material order, drawn as a PDF.
 *
 * Jonathon, 2026-09-02: material orders become "a PDF emailed to the supplier
 * branch contact… sent from the rep's own Gmail with prices". A supplier's
 * counter works off paper: the order gets printed, walked to the racks and
 * ticked off, and a wall of text in an email body is not that. So the order
 * that goes out is a file, with the same lines and the same total the order
 * screen shows.
 *
 * **With prices**, which is Jonathon's choice and the opposite of the crew
 * copy — a supplier is being asked to fill an order at agreed prices, and the
 * quickest way to find out they have changed one is to have written ours down.
 *
 * Drawn in the browser, like the invoice, and for the same reason: nothing on
 * the server can make a PDF. The engine is loaded on demand so a rep opening a
 * job on a driveway does not download it to look at a list.
 */

import {
  COMPANY_NAME,
  formatCents,
  multiplyCents,
  type Cents,
} from "@roof-hawk/core";

import {
  GREY,
  heading,
  INK,
  logoDataUrl,
  MARGIN,
  money,
  PAGE,
  pair,
  RIGHT,
  rule,
  type Doc,
} from "./orderPdfKit";

/** Right edges for the numbers, left edge for the description. */
const COLUMN = {
  item: MARGIN,
  itemWidth: 250,
  qty: 380,
  unitPrice: 480,
  total: RIGHT,
};

const ROWS_TOP = 110;
const ROWS_BOTTOM = PAGE.height - 150;

/** One line, as the counter reads it. */
export interface MaterialOrderPdfLine {
  description: string;
  /** Shown beside the description — the half that gets the wrong roof. */
  color?: string | null;
  qty: number;
  unit: string;
  /** Already cents. The database column is dollars; the caller converts. */
  unitPrice: Cents;
}

export interface MaterialOrderPdf {
  /** "MO-88". */
  number: string;
  supplierName: string | null;
  /** Who it is for and where it goes. */
  jobNumber: number | null;
  jobAddress: string;
  customerName: string | null;
  /** "Pickup at the counter" or "Deliver 2026-09-10". */
  delivery: string;
  deliveryNote: string | null;
  trade: string | null;
  lines: readonly MaterialOrderPdfLine[];
  subtotal: Cents;
  taxRate: number | null;
  tax: Cents;
  total: Cents;
  /** The credit paragraph, when this order is asking for one. */
  note?: string | null;
}

/**
 * The line total, worked out the one way this app works money out.
 *
 * `unitPrice` arrives already in cents — the caller converts, because it is the
 * caller that knows the database column is dollars. Converting again here is
 * how $110.00 becomes $11,000.00, which a test holds down.
 */
export const lineTotal = (line: MaterialOrderPdfLine): Cents =>
  multiplyCents(line.unitPrice, Number(line.qty ?? 0));

function itemsHeader(doc: Doc, y: number): number {
  heading(doc, "Item", COLUMN.item, y);
  doc.setTextColor(GREY);
  doc.setFontSize(10);
  doc.text("Quantity", COLUMN.qty, y, { align: "right" });
  doc.text("Unit price", COLUMN.unitPrice, y, { align: "right" });
  doc.text("Total", COLUMN.total, y, { align: "right" });
  doc.setTextColor(INK);
  rule(doc, y + 10);
  return y + 30;
}

function itemRow(doc: Doc, line: MaterialOrderPdfLine, y: number): number {
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10.5);
  doc.setTextColor(INK);

  const what = line.color
    ? `${line.description} — ${line.color}`
    : line.description;
  const wrapped = doc.splitTextToSize(what || "—", COLUMN.itemWidth);
  doc.text(wrapped, COLUMN.item, y);
  doc.text(`${line.qty ?? 0} ${line.unit ?? ""}`.trim(), COLUMN.qty, y, {
    align: "right",
  });
  doc.text(formatCents(line.unitPrice), COLUMN.unitPrice, y, {
    align: "right",
  });
  doc.text(formatCents(lineTotal(line)), COLUMN.total, y, { align: "right" });

  const height = Math.max(wrapped.length, 1) * 14 + 10;
  rule(doc, y + height - 10);
  return y + height;
}

/** Draw the whole thing. Exported for the test, which reads the words back. */
export async function renderMaterialOrder(
  order: MaterialOrderPdf,
  options: { logo?: string | null } = {},
): Promise<Doc> {
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF({
    unit: "pt",
    format: "letter",
    // Uncompressed, so a test can read the words back out of the file.
    compress: false,
  });

  doc.setProperties({
    title: `Material order ${order.number}`,
    author: COMPANY_NAME,
    subject: `Material order ${order.number}`,
  });

  // ------------------------------------------------------------- the head ---

  doc.setFont("helvetica", "normal");
  doc.setFontSize(26);
  doc.setTextColor(INK);
  doc.text("Material Order", MARGIN, 86);
  doc.setFontSize(14);
  doc.setTextColor(GREY);
  doc.text(order.number, MARGIN, 106);
  doc.setTextColor(INK);

  const logo = options.logo === undefined ? await logoDataUrl() : options.logo;
  if (logo) {
    try {
      doc.addImage(logo, "PNG", RIGHT - 60, 48, 60, 60);
    } catch {
      // A mark that will not draw is not a reason to withhold an order.
    }
  }

  rule(doc, 124);

  pair(doc, "Supplier", order.supplierName ?? "", MARGIN, 150);
  pair(doc, "Ordered by", COMPANY_NAME, 272, 150);
  pair(doc, "Trade", order.trade ?? "", 461, 150);

  pair(
    doc,
    "Job",
    [order.jobNumber ? `#${order.jobNumber}` : "", order.customerName ?? ""]
      .filter(Boolean)
      .join(" · "),
    MARGIN,
    196,
  );
  pair(doc, "Delivery", order.delivery, 272, 196);

  // The address gets its own line: it is the one thing on here a driver reads.
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(GREY);
  doc.text("Address", MARGIN, 242);
  doc.setFontSize(11);
  doc.setTextColor(INK);
  doc.text(order.jobAddress || "—", MARGIN, 257);

  let y = 285;
  if (order.deliveryNote) {
    doc.setFontSize(10.5);
    doc.text(
      doc.splitTextToSize(`Note: ${order.deliveryNote}`, RIGHT - MARGIN),
      MARGIN,
      y,
    );
    y += 24;
  }

  // ------------------------------------------------------------ the items ---

  y = itemsHeader(doc, y + 10);
  for (const line of order.lines) {
    if (y > ROWS_BOTTOM) {
      doc.addPage();
      y = itemsHeader(doc, ROWS_TOP);
    }
    y = itemRow(doc, line, y);
  }
  if (order.lines.length === 0) {
    doc.setFontSize(10.5);
    doc.setTextColor(GREY);
    doc.text("No line items.", COLUMN.item, y);
    doc.setTextColor(INK);
    y += 20;
  }

  // ----------------------------------------------------------- the totals ---

  if (y > ROWS_BOTTOM - 60) {
    doc.addPage();
    y = ROWS_TOP;
  }

  const columns = { label: COLUMN.unitPrice, value: COLUMN.total };
  money(doc, "Subtotal", order.subtotal, y + 22, columns);
  money(
    doc,
    order.taxRate == null ? "Tax" : `Tax (${order.taxRate}%)`,
    order.tax,
    y + 40,
    columns,
  );
  money(doc, "Total", order.total, y + 68, columns, true);

  // ------------------------------------------------------------- the note ---

  if (order.note) {
    const at = Math.min(y + 116, PAGE.height - 96);
    rule(doc, at - 18);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10.5);
    doc.setTextColor(INK);
    doc.text(doc.splitTextToSize(order.note, RIGHT - MARGIN), MARGIN, at);
  }

  return doc;
}

/** The file as base64, which is what the Gmail Edge Function attaches. */
export async function materialOrderPdfBase64(
  order: MaterialOrderPdf,
  options: { logo?: string | null } = {},
): Promise<string> {
  const doc = await renderMaterialOrder(order, options);
  return doc.output("datauristring").split(",")[1] ?? "";
}

/** What the file is called in the supplier's inbox. */
export const materialOrderFilename = (number: string): string =>
  `${number.toLowerCase()}.pdf`;
